'use client'



import { useCallback, useEffect, useRef, useState } from 'react'

import * as signalR from '@microsoft/signalr'
import { signalRSessizKayit } from '@/lib/signalrLogger'

import { getBrowserHubUrl } from '@/lib/pharmacyHubUrl'

import { getSignalRAccessToken, isSignalRUnauthorizedError } from '@/lib/signalrAccessToken'

import { refreshTokenSingleton } from '@/lib/tokenRefreshQueue'
import { rememberNetworkUserAvatarUrl, resolveNetworkProfileMediaUrl, resolveNetworkPostMediaUrl, prefetchNetworkPostImages, collectPostImageUrls } from '@/lib/networkProfileMediaUrl'



export interface PharmacyPostDto {

  id: string

  tenantId: string

  authorGln: string

  authorUserId: string

  authorUsername?: string | null

  authorUserType: string

  authorAvatarUrl?: string | null

  pharmacyDisplayName?: string | null

  pharmacyCity?: string | null

  pharmacyTown?: string | null

  body: string

  editCount: number

  createdAtUtc: string

  updatedAtUtc: string

  editedAtUtc?: string | null

  lastEditedByUsername?: string | null

  wasEdited: boolean

  canEdit: boolean

  canDelete: boolean

  isSuperAdminModerator?: boolean

  isOwnPharmacy: boolean

  isOwnPost: boolean

  likeCount: number

  commentCount: number

  likedByMe: boolean

  dislikeCount: number

  dislikedByMe: boolean

  poll?: PharmacyPostPollDto | null

  images?: PharmacyPostImageDto[]

}



export interface PharmacyPostImageDto {

  id: string

  url: string

}



export interface PharmacyPostPollOptionDto {

  id: string

  label: string

  voteCount: number

}



export interface PharmacyPostPollDto {

  totalVotes: number

  votedOptionId?: string | null

  isClosed: boolean

  endsAtUtc?: string | null

  options: PharmacyPostPollOptionDto[]

}



export interface PharmacyPostCommentDto {

  id: string

  postId: string

  parentId?: string | null

  replyToUsername?: string | null

  tenantId: string

  authorUserId: string

  authorUsername?: string | null

  authorUserType: string

  authorAvatarUrl?: string | null

  pharmacyDisplayName?: string | null

  pharmacyCity?: string | null

  pharmacyTown?: string | null

  body: string

  createdAtUtc: string

  canDelete: boolean

  isSuperAdminModerator?: boolean

  isOwnComment: boolean

  isReply: boolean

  likeCount: number

  likedByMe: boolean

  dislikeCount: number

  dislikedByMe: boolean

  images?: PharmacyPostImageDto[]

}



interface FeedResponse {

  success: boolean

  page: number

  pageSize: number

  totalCount: number

  totalPages: number

  items: PharmacyPostDto[]

  error?: string

}



function normalizePoll(raw: unknown): PharmacyPostPollDto | null | undefined {

  if (raw == null) return null

  if (typeof raw !== 'object') return undefined

  const p = raw as Record<string, unknown>

  const optionsRaw = p.options

  if (!Array.isArray(optionsRaw)) return undefined

  const options = optionsRaw

    .map((o) => {

      if (!o || typeof o !== 'object') return null

      const x = o as Record<string, unknown>

      const id = String(x.id ?? '')

      if (!id) return null

      return {

        id,

        label: String(x.label ?? ''),

        voteCount: Number(x.voteCount ?? 0),

      }

    })

    .filter(Boolean) as PharmacyPostPollOptionDto[]

  return {

    totalVotes: Number(p.totalVotes ?? 0),

    votedOptionId: p.votedOptionId != null ? String(p.votedOptionId) : null,

    isClosed: Boolean(p.isClosed),

    endsAtUtc: p.endsAtUtc != null ? String(p.endsAtUtc) : null,

    options,

  }

}



function normalizePostImages(raw: unknown): PharmacyPostImageDto[] {

  if (!Array.isArray(raw)) return []

  return raw

    .map((item) => {

      if (!item || typeof item !== 'object') return null

      const x = item as Record<string, unknown>

      const id = String(x.id ?? '')

      const urlRaw = x.url != null ? String(x.url) : ''

      if (!id || !urlRaw) return null

      const url = resolveNetworkPostMediaUrl(urlRaw) ?? urlRaw

      return { id, url }

    })

    .filter(Boolean) as PharmacyPostImageDto[]

}



function normalizePost(raw: unknown): PharmacyPostDto | null {

  if (!raw || typeof raw !== 'object') return null

  const p = raw as Record<string, unknown>

  const id = String(p.id ?? '')

  if (!id) return null

  const tenantId = String(p.tenantId ?? '')
  const authorUserId = String(p.authorUserId ?? '')
  const authorAvatarUrlRaw = p.authorAvatarUrl != null ? String(p.authorAvatarUrl) : null
  const authorAvatarUrl = authorAvatarUrlRaw
    ? resolveNetworkProfileMediaUrl(authorAvatarUrlRaw) ?? null
    : null
  if (tenantId && authorUserId && authorAvatarUrl) {
    rememberNetworkUserAvatarUrl(tenantId, authorUserId, authorAvatarUrl)
  }

  return {

    id,

    tenantId,

    authorGln: String(p.authorGln ?? ''),

    authorUserId,

    authorUsername: p.authorUsername != null ? String(p.authorUsername) : null,

    authorUserType: String(p.authorUserType ?? 'tenant'),

    authorAvatarUrl,

    pharmacyDisplayName: p.pharmacyDisplayName != null ? String(p.pharmacyDisplayName) : null,

    pharmacyCity: p.pharmacyCity != null ? String(p.pharmacyCity) : null,

    pharmacyTown: p.pharmacyTown != null ? String(p.pharmacyTown) : null,

    body: String(p.body ?? ''),

    editCount: Number(p.editCount ?? 0),

    createdAtUtc: String(p.createdAtUtc ?? ''),

    updatedAtUtc: String(p.updatedAtUtc ?? ''),

    editedAtUtc: p.editedAtUtc != null ? String(p.editedAtUtc) : null,

    lastEditedByUsername: p.lastEditedByUsername != null ? String(p.lastEditedByUsername) : null,

    wasEdited: Boolean(p.wasEdited),

    canEdit: Boolean(p.canEdit),

    canDelete: Boolean(p.canDelete),

    isSuperAdminModerator: Boolean(p.isSuperAdminModerator),

    isOwnPharmacy: Boolean(p.isOwnPharmacy),

    isOwnPost: Boolean(p.isOwnPost),

    likeCount: Number(p.likeCount ?? 0),

    commentCount: Number(p.commentCount ?? 0),

    likedByMe: Boolean(p.likedByMe),

    dislikeCount: Number(p.dislikeCount ?? 0),

    dislikedByMe: Boolean(p.dislikedByMe),

    poll: normalizePoll(p.poll),

    images: normalizePostImages(p.images),

  }

}



export function normalizePostComment(raw: unknown): PharmacyPostCommentDto | null {

  if (!raw || typeof raw !== 'object') return null

  const c = raw as Record<string, unknown>

  const id = String(c.id ?? '')

  const postId = String(c.postId ?? '')

  if (!id || !postId) return null

  const tenantId = String(c.tenantId ?? '')
  const authorUserId = String(c.authorUserId ?? '')
  const authorAvatarUrlRaw = c.authorAvatarUrl != null ? String(c.authorAvatarUrl) : null
  const authorAvatarUrl = authorAvatarUrlRaw
    ? resolveNetworkProfileMediaUrl(authorAvatarUrlRaw) ?? null
    : null
  if (tenantId && authorUserId && authorAvatarUrl) {
    rememberNetworkUserAvatarUrl(tenantId, authorUserId, authorAvatarUrl)
  }

  return {

    id,

    postId,

    parentId: c.parentId != null ? String(c.parentId) : null,

    replyToUsername: c.replyToUsername != null ? String(c.replyToUsername) : null,

    tenantId,

    authorUserId,

    authorUsername: c.authorUsername != null ? String(c.authorUsername) : null,

    authorUserType: String(c.authorUserType ?? 'tenant'),

    authorAvatarUrl,

    pharmacyDisplayName: c.pharmacyDisplayName != null ? String(c.pharmacyDisplayName) : null,

    pharmacyCity: c.pharmacyCity != null ? String(c.pharmacyCity) : null,

    pharmacyTown: c.pharmacyTown != null ? String(c.pharmacyTown) : null,

    body: String(c.body ?? ''),

    createdAtUtc: String(c.createdAtUtc ?? ''),

    canDelete: Boolean(c.canDelete),

    isSuperAdminModerator: Boolean(c.isSuperAdminModerator),

    isOwnComment: Boolean(c.isOwnComment),

    isReply: Boolean(c.isReply ?? c.parentId),

    likeCount: Number(c.likeCount ?? 0),

    likedByMe: Boolean(c.likedByMe),

    dislikeCount: Number(c.dislikeCount ?? 0),

    dislikedByMe: Boolean(c.dislikedByMe),

    images: normalizePostImages(c.images),

  }

}



function parseFeedError(raw: unknown): string {

  if (raw == null || raw === '') return 'Akış yüklenemedi'

  if (typeof raw === 'string') {

    try {

      const parsed = JSON.parse(raw) as { error?: string; detail?: string; title?: string }

      if (parsed.error) return parsed.error

      if (parsed.detail) return parsed.detail

      if (parsed.title) return parsed.title

    } catch {

      if (raw.startsWith('{') || raw.startsWith('<') || raw.length > 160) return 'Akış yüklenemedi'

      return raw

    }

  }

  return 'Akış yüklenemedi'

}



export function usePharmacyNetworkFeed(enabled: boolean) {

  const [posts, setPosts] = useState<PharmacyPostDto[]>([])

  const [commentsByPost, setCommentsByPost] = useState<Record<string, PharmacyPostCommentDto[]>>({})

  const [loading, setLoading] = useState(true)

  const [loadingMore, setLoadingMore] = useState(false)

  const [page, setPage] = useState(1)

  const [totalPages, setTotalPages] = useState(0)

  const [connected, setConnected] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const connRef = useRef<signalR.HubConnection | null>(null)

  const myUserIdRef = useRef<string | null>(null)



  useEffect(() => {

    if (typeof window !== 'undefined') {

      myUserIdRef.current = localStorage.getItem('userId')

    }

  }, [])

  useEffect(() => {
    if (!enabled || posts.length === 0) return
    prefetchNetworkPostImages(collectPostImageUrls(posts))
  }, [enabled, posts])

  const mergePosts = useCallback((incoming: PharmacyPostDto[]) => {

    setPosts((prev) => {

      const map = new Map<string, PharmacyPostDto>()

      for (const p of prev) map.set(p.id, p)

      for (const p of incoming) map.set(p.id, p)

      return [...map.values()].sort(

        (a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime()

      )

    })

  }, [])



  const loadPage = useCallback(async (pageNum: number, append: boolean) => {

    if (pageNum === 1) setLoading(true)

    else setLoadingMore(true)

    setError(null)

    try {

      const params = new URLSearchParams({ page: String(pageNum), pageSize: '20' })

      const res = await fetch(`/api/opas/pharmacy-posts?${params.toString()}`, {

        credentials: 'include',

        cache: 'no-store',

      })

      const data = (await res.json()) as FeedResponse

      if (!res.ok) throw new Error(parseFeedError(data.error))

      const items = (data.items ?? []).map((x) => normalizePost(x)).filter(Boolean) as PharmacyPostDto[]

      if (append) mergePosts(items)

      else setPosts(items)

      setPage(data.page)

      setTotalPages(data.totalPages)

    } catch (e) {

      setError(e instanceof Error ? e.message : 'Bağlantı hatası')

    } finally {

      setLoading(false)

      setLoadingMore(false)

    }

  }, [mergePosts])



  useEffect(() => {

    if (!enabled) {

      setLoading(false)

      setError(null)

      return

    }

    setLoading(true)

    setError(null)

    void loadPage(1, false)

  }, [enabled, loadPage])



  useEffect(() => {

    if (!enabled) {

      setConnected(false)

      void connRef.current?.stop()

      connRef.current = null

      return

    }



    let cancelled = false



    async function connect() {

      try {

        const hubUrl = getBrowserHubUrl('/hubs/pharmacy-network')

        if (!hubUrl) return

        const initial = await getSignalRAccessToken()

        if (!initial) return



        const connection = new signalR.HubConnectionBuilder()

          .withUrl(hubUrl, {

            accessTokenFactory: async () => (await getSignalRAccessToken()) ?? '',

          })

          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])

          .configureLogging(signalRSessizKayit)

          .build()



        connection.on('ReceiveNetworkPost', (payload: unknown) => {

          const n = normalizePost(payload)

          if (!n) return

          prefetchNetworkPostImages(collectPostImageUrls([n]))

          setPosts((prev) => {

            if (prev.some((p) => p.id === n.id)) return prev

            return [n, ...prev]

          })

        })



        connection.on('NetworkPostUpdated', (payload: unknown) => {

          const n = normalizePost(payload)

          if (!n) return

          setPosts((prev) => prev.map((p) => (p.id === n.id ? { ...n, likeCount: p.likeCount, commentCount: p.commentCount, likedByMe: p.likedByMe, dislikeCount: p.dislikeCount, dislikedByMe: p.dislikedByMe } : p)))

        })



        connection.on('NetworkPostDeleted', (payload: unknown) => {

          const id = (payload as { id?: string })?.id

          if (!id) return

          setPosts((prev) => prev.filter((p) => p.id !== id))

          setCommentsByPost((prev) => {

            const next = { ...prev }

            delete next[id]

            return next

          })

        })



        connection.on('NetworkPostLikeChanged', (payload: unknown) => {

          const p = payload as { postId?: string; likeCount?: number; liked?: boolean; userId?: string }

          if (!p.postId) return

          const myId = myUserIdRef.current

          setPosts((prev) =>

            prev.map((post) => {

              if (post.id !== p.postId) return post

              const likedByMe =

                myId && p.userId === myId && typeof p.liked === 'boolean' ? p.liked : post.likedByMe

              const dislikedByMe =

                myId && p.userId === myId && p.liked === true ? false : post.dislikedByMe

              return {

                ...post,

                likeCount: Number(p.likeCount ?? post.likeCount),

                likedByMe,

                dislikedByMe,

              }

            })

          )

        })



        connection.on('NetworkPostDislikeChanged', (payload: unknown) => {

          const p = payload as { postId?: string; dislikeCount?: number; disliked?: boolean; userId?: string }

          if (!p.postId) return

          const myId = myUserIdRef.current

          setPosts((prev) =>

            prev.map((post) => {

              if (post.id !== p.postId) return post

              const dislikedByMe =

                myId && p.userId === myId && typeof p.disliked === 'boolean' ? p.disliked : post.dislikedByMe

              const likedByMe =

                myId && p.userId === myId && p.disliked === true ? false : post.likedByMe

              return {

                ...post,

                dislikeCount: Number(p.dislikeCount ?? post.dislikeCount),

                dislikedByMe,

                likedByMe,

              }

            })

          )

        })



        connection.on('ReceiveNetworkPostComment', (payload: unknown) => {

          const c = normalizePostComment(payload)

          if (!c) return

          setCommentsByPost((prev) => {

            const list = prev[c.postId] ?? []

            if (list.some((x) => x.id === c.id)) return prev

            return { ...prev, [c.postId]: [...list, c] }

          })

        })



        connection.on('NetworkPostCommentCountChanged', (payload: unknown) => {

          const p = payload as { postId?: string; commentCount?: number }

          if (!p.postId) return

          setPosts((prev) =>

            prev.map((post) =>

              post.id === p.postId ? { ...post, commentCount: Number(p.commentCount ?? post.commentCount) } : post

            )

          )

        })



        connection.on('NetworkPostCommentDeleted', (payload: unknown) => {

          const p = payload as { postId?: string; commentId?: string; commentCount?: number }

          if (!p.postId || !p.commentId) return

          setCommentsByPost((prev) => {

            const list = prev[p.postId!]

            if (!list) return prev

            const removeIds = new Set<string>([p.commentId!])

            let changed = true

            while (changed) {

              changed = false

              for (const c of list) {

                if (c.parentId && removeIds.has(c.parentId) && !removeIds.has(c.id)) {

                  removeIds.add(c.id)

                  changed = true

                }

              }

            }

            return { ...prev, [p.postId!]: list.filter((c) => !removeIds.has(c.id)) }

          })

          setPosts((prev) =>

            prev.map((post) =>

              post.id === p.postId ? { ...post, commentCount: Number(p.commentCount ?? post.commentCount) } : post

            )

          )

        })



        connection.on('NetworkCommentLikeChanged', (payload: unknown) => {

          const p = payload as { postId?: string; commentId?: string; likeCount?: number; liked?: boolean; userId?: string }

          if (!p.postId || !p.commentId) return

          const myId = myUserIdRef.current

          setCommentsByPost((prev) => {

            const list = prev[p.postId!]

            if (!list) return prev

            return {

              ...prev,

              [p.postId!]: list.map((c) => {

                if (c.id !== p.commentId) return c

                const likedByMe =

                  myId && p.userId === myId && typeof p.liked === 'boolean' ? p.liked : c.likedByMe

                const dislikedByMe =

                  myId && p.userId === myId && p.liked === true ? false : c.dislikedByMe

                return { ...c, likeCount: Number(p.likeCount ?? c.likeCount), likedByMe, dislikedByMe }

              }),

            }

          })

        })



        connection.on('NetworkCommentDislikeChanged', (payload: unknown) => {

          const p = payload as { postId?: string; commentId?: string; dislikeCount?: number; disliked?: boolean; userId?: string }

          if (!p.postId || !p.commentId) return

          const myId = myUserIdRef.current

          setCommentsByPost((prev) => {

            const list = prev[p.postId!]

            if (!list) return prev

            return {

              ...prev,

              [p.postId!]: list.map((c) => {

                if (c.id !== p.commentId) return c

                const dislikedByMe =

                  myId && p.userId === myId && typeof p.disliked === 'boolean' ? p.disliked : c.dislikedByMe

                const likedByMe =

                  myId && p.userId === myId && p.disliked === true ? false : c.likedByMe

                return { ...c, dislikeCount: Number(p.dislikeCount ?? c.dislikeCount), dislikedByMe, likedByMe }

              }),

            }

          })

        })



        connection.on('NetworkPollVoteChanged', (payload: unknown) => {

          const p = payload as { postId?: string; poll?: unknown }

          if (!p.postId) return

          const poll = normalizePoll(p.poll)

          if (!poll) return

          setPosts((prev) =>

            prev.map((post) => (post.id === p.postId ? { ...post, poll } : post))

          )

        })



        connection.onreconnecting(() => setConnected(false))

        connection.onreconnected(() => setConnected(true))



        try {

          await connection.start()

        } catch (e) {

          if (isSignalRUnauthorizedError(e)) {

            await refreshTokenSingleton()

            await connection.start()

          } else {

            throw e

          }

        }

        if (cancelled) {

          await connection.stop()

          return

        }

        connRef.current = connection

        setConnected(true)

      } catch {

        setConnected(false)

      }

    }



    void connect()

    return () => {

      cancelled = true

      void connRef.current?.stop()

      connRef.current = null

    }

  }, [enabled])



  const loadMore = useCallback(() => {

    if (loadingMore || page >= totalPages) return

    void loadPage(page + 1, true)

  }, [loadPage, loadingMore, page, totalPages])



  const createPost = useCallback(async (body: string, pollOptions?: string[], imageDataUrls?: string[]) => {

    const payload: { body: string; poll?: { options: string[] }; imageDataUrls?: string[] } = { body }

    if (pollOptions && pollOptions.length >= 2) payload.poll = { options: pollOptions }

    if (imageDataUrls && imageDataUrls.length > 0) payload.imageDataUrls = imageDataUrls

    const res = await fetch('/api/opas/pharmacy-posts', {

      method: 'POST',

      credentials: 'include',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify(payload),

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Paylaşım gönderilemedi')

    const item = normalizePost(data.item)

    if (item) {

      setPosts((prev) => {

        if (prev.some((p) => p.id === item.id)) return prev

        return [item, ...prev]

      })

    }

    return item

  }, [])



  const updatePost = useCallback(async (postId: string, body: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}`, {

      method: 'PATCH',

      credentials: 'include',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ body }),

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Güncellenemedi')

    const item = normalizePost(data.item)

    if (item) {

      setPosts((prev) =>

        prev.map((p) =>

          p.id === item.id

            ? { ...item, likeCount: p.likeCount, commentCount: p.commentCount, likedByMe: p.likedByMe, dislikeCount: p.dislikeCount, dislikedByMe: p.dislikedByMe }

            : p

        )

      )

    }

    return item

  }, [])



  const deletePost = useCallback(async (postId: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}`, {

      method: 'DELETE',

      credentials: 'include',

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Silinemedi')

    setPosts((prev) => prev.filter((p) => p.id !== postId))

    setCommentsByPost((prev) => {

      const next = { ...prev }

      delete next[postId]

      return next

    })

  }, [])



  const toggleLike = useCallback(async (postId: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/like`, {

      method: 'POST',

      credentials: 'include',

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Beğeni işlemi başarısız')

    setPosts((prev) =>

      prev.map((p) =>

        p.id === postId

          ? { ...p, likeCount: Number(data.likeCount ?? p.likeCount), likedByMe: Boolean(data.likedByMe), dislikeCount: Number(data.dislikeCount ?? p.dislikeCount), dislikedByMe: Boolean(data.dislikedByMe ?? false) }

          : p

      )

    )

  }, [])



  const toggleDislike = useCallback(async (postId: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/dislike`, {

      method: 'POST',

      credentials: 'include',

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Beğenmeme işlemi başarısız')

    setPosts((prev) =>

      prev.map((p) =>

        p.id === postId

          ? {

              ...p,

              dislikeCount: Number(data.dislikeCount ?? p.dislikeCount),

              dislikedByMe: Boolean(data.dislikedByMe),

              likeCount: Number(data.likeCount ?? p.likeCount),

              likedByMe: Boolean(data.likedByMe ?? false),

            }

          : p

      )

    )

  }, [])



  const reportPost = useCallback(async (postId: string, reason: string, note?: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/report`, {

      method: 'POST',

      credentials: 'include',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ reason, note: note ?? null }),

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Bildirim gönderilemedi')

    return Boolean(data.alreadyReported)

  }, [])



  const loadComments = useCallback(async (postId: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments?take=50`, {

      credentials: 'include',

      cache: 'no-store',

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Yorumlar yüklenemedi')

    const items = (data.items ?? []).map((x: unknown) => normalizePostComment(x)).filter(Boolean) as PharmacyPostCommentDto[]

    setCommentsByPost((prev) => ({ ...prev, [postId]: items }))

    return items

  }, [])



  const addComment = useCallback(async (postId: string, body: string, parentId?: string | null, imageDataUrls?: string[]) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments`, {

      method: 'POST',

      credentials: 'include',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ body, parentId: parentId ?? null, imageDataUrls: imageDataUrls?.length ? imageDataUrls : undefined }),

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Yorum gönderilemedi')

    const item = normalizePostComment(data.item)

    if (item) {

      setCommentsByPost((prev) => {

        const list = prev[postId] ?? []

        if (list.some((c) => c.id === item.id)) return prev

        return { ...prev, [postId]: [...list, item] }

      })

      setPosts((prev) =>

        prev.map((p) =>

          p.id === postId ? { ...p, commentCount: Number(data.commentCount ?? p.commentCount + 1) } : p

        )

      )

    }

    return item

  }, [])



  const deleteComment = useCallback(async (postId: string, commentId: string) => {

    const res = await fetch(

      `/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,

      { method: 'DELETE', credentials: 'include' }

    )

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Yorum silinemedi')

    setCommentsByPost((prev) => {

      const list = prev[postId] ?? []

      const removeIds = new Set<string>([commentId])

      let changed = true

      while (changed) {

        changed = false

        for (const c of list) {

          if (c.parentId && removeIds.has(c.parentId) && !removeIds.has(c.id)) {

            removeIds.add(c.id)

            changed = true

          }

        }

      }

      return {

        ...prev,

        [postId]: list.filter((c) => !removeIds.has(c.id)),

      }

    })

    setPosts((prev) =>

      prev.map((p) =>

        p.id === postId ? { ...p, commentCount: Number(data.commentCount ?? Math.max(0, p.commentCount - 1)) } : p

      )

    )

  }, [])



  const toggleCommentLike = useCallback(async (postId: string, commentId: string) => {

    const res = await fetch(

      `/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/like`,

      { method: 'POST', credentials: 'include' }

    )

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Beğeni işlemi başarısız')

    setCommentsByPost((prev) => ({

      ...prev,

      [postId]: (prev[postId] ?? []).map((c) =>

        c.id === commentId

          ? { ...c, likeCount: Number(data.likeCount ?? c.likeCount), likedByMe: Boolean(data.likedByMe), dislikeCount: Number(data.dislikeCount ?? c.dislikeCount), dislikedByMe: Boolean(data.dislikedByMe ?? false) }

          : c

      ),

    }))

  }, [])



  const toggleCommentDislike = useCallback(async (postId: string, commentId: string) => {

    const res = await fetch(

      `/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/dislike`,

      { method: 'POST', credentials: 'include' }

    )

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Beğenmeme işlemi başarısız')

    setCommentsByPost((prev) => ({

      ...prev,

      [postId]: (prev[postId] ?? []).map((c) =>

        c.id === commentId

          ? {

              ...c,

              dislikeCount: Number(data.dislikeCount ?? c.dislikeCount),

              dislikedByMe: Boolean(data.dislikedByMe),

              likeCount: Number(data.likeCount ?? c.likeCount),

              likedByMe: Boolean(data.likedByMe ?? false),

            }

          : c

      ),

    }))

  }, [])



  const reportComment = useCallback(async (postId: string, commentId: string, reason: string, note?: string) => {

    const res = await fetch(

      `/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/report`,

      {

        method: 'POST',

        credentials: 'include',

        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ reason, note: note ?? null }),

      }

    )

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Bildirim gönderilemedi')

    return Boolean(data.alreadyReported)

  }, [])



  const votePoll = useCallback(async (postId: string, optionId: string) => {

    const res = await fetch(`/api/opas/pharmacy-posts/${encodeURIComponent(postId)}/poll/vote`, {

      method: 'POST',

      credentials: 'include',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ optionId }),

    })

    const data = await res.json()

    if (!res.ok) throw new Error(data.error ?? 'Oy kullanılamadı')

    const poll = normalizePoll(data.poll)

    if (poll) {

      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, poll } : p)))

    }

  }, [])



  return {

    posts,

    commentsByPost,

    loading,

    loadingMore,

    connected,

    error,

    hasMore: page < totalPages,

    loadMore,

    createPost,

    updatePost,

    deletePost,

    toggleLike,

    toggleDislike,

    reportPost,

    loadComments,

    addComment,

    deleteComment,

    toggleCommentLike,

    toggleCommentDislike,

    reportComment,

    votePoll,

    refresh: () => loadPage(1, false),

  }

}



